import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BartenderMediaGallery from '../components/BartenderMediaGallery';

const BartenderDashboard = ({ user }) => {
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('bookings');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, bookingsRes] = await Promise.all([
        axios.get(`/bartender/profile/${user.id}`),
        axios.get('/bartender/bookings')
      ]);
      
      setProfile(profileRes.data);
      setBookings(bookingsRes.data.bookings || []);
    } catch (err) {
      console.error('Failed to fetch bartender data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Bartender Dashboard 🍸</h1>
        <p className="mt-2 text-gray-600">Welcome back, {user.name}!</p>
      </div>

      {/* Subscription Status Banner */}
      {!profile?.subscription_active && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Subscription Required:</strong> Subscribe for $30/month to start accepting bookings. Contact admin for assistance.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* License Verification Status */}
      {!profile?.license_verified && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>License Pending Verification:</strong> Your bartender license is being reviewed by our admin team.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'bookings'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            My Bookings ({bookings.length})
          </button>
          <button
            onClick={() => setActiveTab('photos')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'photos'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Bar Setup Photos
          </button>
          
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Settings
          </button>
        </nav>
      </div>

      {/* Bookings Tab */}
      {activeTab === 'bookings' && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Your Bookings</h2>
          {bookings.length > 0 ? (
            <div className="grid gap-4">
              {bookings.map((booking) => (
                <div key={booking.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-500">Booking ID: {booking.id}</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">
                        {booking.duration} Booking
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        📅 {new Date(booking.date).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-gray-600">
                        📍 {booking.service_city}, {booking.service_state} {booking.service_zip}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        booking.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {booking.status}
                      </span>
                      <p className="text-lg font-bold text-gray-900 mt-2">
                        ${(booking.operational_fee_cents / 100).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">operational fee</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-12 text-center">
              <p className="text-gray-500">No bookings yet. Complete your profile to start receiving bookings!</p>
            </div>
          )}
        </div>
      )}

      {/* Bar Photos Tab */}
      {activeTab === 'photos' && (
        <div>
          <BartenderMediaGallery user={user} profile={profile} onUpdate={fetchData} />
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">⚙️ Settings & License Management</h2>
            
            <div className="space-y-6">
              {/* License Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">🪪 Bartender License</h3>
                
                {profile?.license_url ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-semibold text-green-900">✅ License on File</p>
                        <p className="text-xs text-green-600 mt-2">
                          If you uploaded the wrong license, delete it and contact admin to upload the correct one.
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Delete your bartender license? Contact admin to re-upload.')) return;
                          try {
                            await axios.delete('/bartender/license');
                            alert('✅ License deleted. Contact admin to upload correct license.');
                            fetchData();
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Failed to delete license');
                          }
                        }}
                        className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm font-semibold"
                      >
                        🗑️ Delete License
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800">⚠️ No license on file. Contact admin to add your license.</p>
                  </div>
                )}
              </div>

              {/* LLC Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">🏢 LLC Documentation</h3>
                
                {profile?.llc_url ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-semibold text-green-900">✅ LLC on File</p>
                        <p className="text-xs text-green-600 mt-2">
                          If you uploaded the wrong LLC document, delete it and contact admin to upload the correct one.
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Delete your LLC document? Contact admin to re-upload.')) return;
                          try {
                            await axios.delete('/bartender/llc');
                            alert('✅ LLC document deleted. Contact admin to upload correct document.');
                            fetchData();
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Failed to delete LLC');
                          }
                        }}
                        className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm font-semibold"
                      >
                        🗑️ Delete LLC
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800">⚠️ No LLC on file. Contact admin to add your LLC.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BartenderDashboard;
